import {
  Accordion,
  AccordionItem,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  Chip,
  Divider,
  Input,
  Select,
  SelectItem,
} from '@nextui-org/react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { FormEvent, useEffect, useMemo } from 'react';
import { Form } from '@nextui-org/form';
import {
  selectClientProfile,
  selectCurrentClient,
} from '../../store/client/clientSlice';
import {
  clientProfile,
  updateClientProfile,
} from '../../store/chat/chatThunks';
import {
  Chart as ChartJS,
  Colors,
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import 'chartjs-adapter-moment';
import ClientProfileSentimentChart from './ClientProfileSentimentChart';
import { capitalizeFirstLetter, GENDER_LABELS } from '../../util';
import ClientProfileSuicidalityChart from './ClientProfileSuicidalityChart';
import ClientProfileTrafficLightChart from './ClientProfileTrafficLightChart';
import { selectSettingsAnalytics } from '../../store/settings/settingsSlice';

ChartJS.register(
  Colors,
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export default function ClientProfile() {
  const dispatch = useAppDispatch();
  const currentClient = useAppSelector(selectCurrentClient);

  const settingsAnalytics = useAppSelector(selectSettingsAnalytics);

  const showSentiment = settingsAnalytics.autoEvaluateSentiment;

  const showSuicidality = settingsAnalytics.evaluateSuicidality;

  const showTrafficLight = settingsAnalytics.autoTrafficLight;

  const clientName = useMemo(() => {
    if (currentClient?.userType === 'actor') {
      return currentClient.name || currentClient.username;
    }

    return (
      currentClient?.friendlyName ||
      currentClient?.username ||
      currentClient?.userId
    );
  }, [currentClient]);

  const userId = currentClient
    ? currentClient.isCustomActor
      ? undefined
      : currentClient.userId
    : undefined;
  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const data = Object.fromEntries(new FormData(e.currentTarget));
    const {
      age,
      gender,
      address,
      ethnicity,
      language_preferred,
      ses,
      religion,
      occupation,
      relationship_status,
    } = data;

    const defaultProfile = {
      gender: profile.gender?.toString() || 'undisclosed',
      address: profile.address?.toString() || null,
      ethnicity: profile.ethnicity?.toString() || null,
      language_preferred: profile.language_preferred?.toString() || null,
      ses: profile.ses?.toString() || null,
      religion: profile.religion?.toString() || null,
      occupation: profile.occupation?.toString() || null,
      accommodation: profile.accommodation?.toString() || null,
      relationship_status: profile.relationship_status?.toString() || null,
    };

    dispatch(
      updateClientProfile(
        {
          age: parseInt(age.toString()),
          gender: gender ? gender.toString() : defaultProfile.gender,
          address: address.toString() || defaultProfile.address,
          ethnicity: ethnicity.toString() || defaultProfile.ethnicity,
          language_preferred:
            language_preferred.toString() || defaultProfile.language_preferred,
          ses: ses.toString() || defaultProfile.ses,
          religion: religion.toString() || defaultProfile.religion,
          occupation: occupation.toString() || defaultProfile.occupation,
          accommodation: defaultProfile.accommodation,
          relationship_status:
            relationship_status.toString() ||
            defaultProfile.relationship_status,
        },
        userId
      )
    );
  };

  useEffect(() => {
    dispatch(clientProfile(userId, currentClient?.userType === 'actor'));
  }, [dispatch, userId, currentClient]);

  const profile = useAppSelector(selectClientProfile);
  const overview = profile.summary
    ? profile.summary
    : 'Client Summary not available.';

  const clientOverviewAccordion = (
    <Accordion
      variant="splitted"
      className="py-2 gap-4"
      itemClasses={{ title: 'text-medium' }}
    >
      <AccordionItem key="1" aria-label="Client Summary" title="Client Summary">
        <span>{overview}</span>
      </AccordionItem>
    </Accordion>
  );

  const userForm = currentClient && (
    <Form key={currentClient.userId} onSubmit={handleSubmit}>
      {clientOverviewAccordion}

      <Input
        label="Age"
        name="age"
        type="number"
        value={profile.age?.toString()}
      />

      <Input
        label="Address"
        name="address"
        type="string"
        placeholder={profile.address?.toString()}
        defaultValue={profile.address?.toString()}
      />

      <Select
        label="Gender"
        name="gender"
        placeholder={capitalizeFirstLetter(profile.gender?.toString())}
        items={Object.entries(GENDER_LABELS).map((o) => ({
          key: o[0],
          label: o[1].label,
        }))}
      >
        {(item) => <SelectItem key={item.key}>{item.label}</SelectItem>}
      </Select>

      <Input
        label="Ethnicity"
        name="ethnicity"
        type="string"
        placeholder={profile.ethnicity?.toString()}
        defaultValue={profile.ethnicity?.toString()}
      />

      <Input
        label="Language Preferred"
        name="language_preferred"
        type="string"
        placeholder={profile.language_preferred?.toString()}
        defaultValue={profile.language_preferred?.toString()}
      />

      <Input
        label="SES"
        name="ses"
        type="string"
        placeholder={profile.ses?.toString()}
        defaultValue={profile.ses?.toString()}
      />

      <Input
        label="Religion"
        name="religion"
        type="string"
        placeholder={profile.religion?.toString()}
        defaultValue={profile.religion?.toString()}
      />

      <Input
        label="Occupation"
        name="occupation"
        type="string"
        placeholder={profile.occupation?.toString()}
        defaultValue={profile.occupation?.toString()}
      />

      <Input
        label="Relationship Status"
        name="relationship_status"
        type="string"
        placeholder={profile.relationship_status?.toString()}
        defaultValue={profile.relationship_status?.toString()}
      />

      <Divider />

      <div className="flex flex-row justify-between w-full gap-2">
        <ButtonGroup>
          <Button
            isDisabled={currentClient.userType === 'actor'}
            type="submit"
            color="primary"
          >
            Update Profile
          </Button>
        </ButtonGroup>
      </div>
    </Form>
  );

  const accordion = (
    <Accordion
      variant="splitted"
      className="py-2 gap-4"
      itemClasses={{ title: 'text-medium' }}
    >
      <AccordionItem
        key="1"
        aria-label="Profile Information"
        title="Profile Information"
      >
        {userForm}
      </AccordionItem>

      <AccordionItem
        key="2"
        aria-label="Sentiment analysis"
        title="Sentiment (mood) analysis"
        className={`${showSentiment ? 'sentiment-enabled' : 'sentiment-disabled'}`}
      >
        <ClientProfileSentimentChart />
      </AccordionItem>

      <AccordionItem
        key="3"
        aria-label="Suicidality analysis"
        title="Suicidality analysis"
        className={`${showSuicidality ? 'suicidality-enabled' : 'suicidality-disabled'}`}
      >
        <ClientProfileSuicidalityChart />
      </AccordionItem>

      <AccordionItem
        key="4"
        aria-label="Traffic Light analysis"
        title="Traffic Light analysis"
        className={`${showTrafficLight ? 'traffic-light-enabled' : 'traffic-light-disabled'}`}
      >
        <ClientProfileTrafficLightChart />
      </AccordionItem>
    </Accordion>
  );

  return (
    <Card className="relative w-full h-full">
      {currentClient ? (
        <>
          <CardHeader className="gap-2">
            <p className="text-lg font-semibold">{clientName}</p>
          </CardHeader>
          <Divider />
          <CardBody>{accordion}</CardBody>
        </>
      ) : (
        <CardBody className="w-full h-full">
          <Chip variant="flat" className="m-auto">
            Select a Client
          </Chip>
        </CardBody>
      )}
    </Card>
  );
}
